import { Box } from "@bcc-ui/ui-components";
import * as React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import AllReportsTable from "./AllReportsTable";
import CustomReport from "./custom/CustomReport";
import style from "./erasure-reports-table.scss";
import ManageEditReportView from "./ManageEditReportView";
import KebabMenu from "components/kebab-menu/KebabMenu";
import ApplicationLayout from "components/layout/ApplicationLayout";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import { VideoTutorialButton } from "components/modal/video-modal/video-tutorial-button/VideoTutorialButton";
import { VideoModal } from "components/modal/video-modal/VideoModal";
import ExportReportsView from "components/reports/export/ExportReportsView";
import ImportReportsView from "components/reports/import/ImportReportsView";
import ReportViewKebabMenu from "components/reports/views/ReportViewKebabMenu";
import { ALL_REPORTS_ROUTE, REPORTS_ROUTE } from "components/router/Routes";
import { AUTH_REPORT_EXPORT_ALL, AUTH_REPORT_IMPORT } from "domain/authority";
import { CustomReportView, Path } from "domain/reports";
import { TutorialVideoLink } from "domain/tutorialVideos";
import { reportService } from "services/report/erasure/ReportService";
import { reportDeletionService } from "services/report/ReportDeletionService";
import { getReportUuidsToBeDeleted, setReportUuidsToBeDeleted } from "services/table/tableRepository";
import { hasTenantCookie } from "services/tenants/tenantCookieService";
import { userSessionService } from "services/user/UserSessionService";

export interface Props {
    viewDetails: CustomReportView;
}

export interface ExportDetails {
    startDate?: string;
    endDate?: string;
    searchQuery: string;
}

export default function AllReportsView(props?: Props): JSX.Element {
    const { t } = useTranslation();
    const [loading, setLoading] = React.useState(true);
    const [state, setState] = useState({ count: 0 });
    const [refreshingCount, setRefreshingCount] = React.useState(0);
    const [showVideoModal, setShowVideoModal] = React.useState(false);
    let viewTitle = t("Common.allReports");
    let viewDetails: CustomReportView = {
        uuid: "",
        name: "",
        columns: [
            "uuid",
            "product",
            "date",
            Path.ERASURE_STATE,
            Path.SYSTEM_MANUFACTURER,
            Path.SYSTEM_MODEL,
            Path.DISK_CAPACITY,
        ],
        shared: false,
        own: true,
    };
    const [exportDetails, setExportDetails] = React.useState<ExportDetails>();
    const [exportVisibility, setExportVisibility] = React.useState(false);
    const [totalReportCount, setTotalReportCount] = React.useState<number>(0);
    const [disableExportReportButton, setDisableExportReportButton] = React.useState(false);
    if (props != undefined && props.viewDetails != undefined) {
        viewTitle = props.viewDetails.name;
        viewDetails = props.viewDetails;
    }
    const [show, setShow] = React.useState<boolean>(false);
    const [editReportUuid, setEditReportUuid] = React.useState<string>("");
    const watchTutorialButton = <VideoTutorialButton onClick={() => setShowVideoModal(true)} />;

    function onViewDelete() {
        window.location.replace(ALL_REPORTS_ROUTE.path);
    }

    function onDetailsChanged(exportDetails: ExportDetails) {
        setExportDetails(exportDetails);
    }

    const kebabMenu = props?.viewDetails && (
        <Box className={style.kebabMenuContainer}>
            <KebabMenu>
                <ReportViewKebabMenu
                    name={viewTitle}
                    viewDetails={props.viewDetails || viewDetails}
                    onViewDelete={onViewDelete}
                    shared={props.viewDetails.shared || false}
                />
            </KebabMenu>
        </Box>
    );

    const createViewActions = () => {
        const exportButton = userSessionService.userHasAllAuthorities([AUTH_REPORT_EXPORT_ALL]) &&
            !hasTenantCookie() &&
            exportVisibility && (
                <ExportReportsView
                    isAllReportsView={props?.viewDetails?.uuid == "undefined" || props?.viewDetails?.uuid == ""}
                    columns={props ? props?.viewDetails.columns : viewDetails.columns}
                    filters={props?.viewDetails?.filters}
                    startDate={exportDetails?.startDate}
                    searchQuery={exportDetails ? exportDetails.searchQuery : ""}
                    endDate={exportDetails?.endDate}
                    exportAllButton={true}
                    totalReportCount={totalReportCount}
                    disableExportReportButton={disableExportReportButton}
                />
            );

        const importButton = !hasTenantCookie() && userSessionService.userHasAllAuthorities([AUTH_REPORT_IMPORT]) && (
            <ImportReportsView />
        );

        const customReportButton = !hasTenantCookie() &&
            userSessionService.userHasAllAuthorities([AUTH_REPORT_IMPORT]) && <CustomReport />;

        return (
            <>
                {viewTitle === t("Common.allReports") ? watchTutorialButton : undefined}
                {exportButton}
                {importButton}
                {customReportButton}
            </>
        );
    };

    function onReportDelete() {
        setState((prevState) => ({ count: prevState.count - 1 }));
    }

    function onRefreshing() {
        setRefreshingCount((prevState) => prevState + 1);
    }

    React.useEffect(() => {
        const abortController = new AbortController();
        reportDeletionService
            .fetchReportsUnderDeletion(abortController)
            .then((reports: string[]) => {
                if (reports.length > 0) {
                    setReportUuidsToBeDeleted(getReportUuidsToBeDeleted().concat(reports));
                }
            })
            .finally(() => setLoading(false));
        return () => abortController.abort();
    }, []);

    return (
        <>
            <ApplicationLayout
                viewTitle={!show ? viewTitle : undefined}
                view={
                    !show ? (
                        <AllReportsTable
                            count={state.count}
                            fetchAllReportsService={reportService}
                            viewDetails={viewDetails}
                            onReportDelete={onReportDelete}
                            refreshCount={refreshingCount}
                            onDetailsChanged={onDetailsChanged}
                            setExportVisibility={setExportVisibility}
                            setTotalReportCount={setTotalReportCount}
                            setDisableExportReportButton={setDisableExportReportButton}
                            showForm={setShow}
                            editReportUuid={setEditReportUuid}
                            hideDialogView={setShow}
                        />
                    ) : loading ? (
                        <LoadingIndicator />
                    ) : (
                        <ManageEditReportView hideDialogView={setShow} reportUuid={editReportUuid} />
                    )
                }
                mainRoute={REPORTS_ROUTE}
                kebabMenu={!show ? kebabMenu : undefined}
                viewActions={!show ? createViewActions() : undefined}
                hideActions={watchTutorialButton}
                enableRefresh={!show}
                onHandleRefresh={onRefreshing}
            />
            <VideoModal
                isOpen={showVideoModal}
                hideModal={() => setShowVideoModal(false)}
                linkUrl={TutorialVideoLink.REPORTS}
            />
        </>
    );
}
